import { useEffect, useRef, useState } from "react";
import { useCouvre } from "./messager.js";
import type { Coords, Fix } from "@hoa/protocol";
import { bearing, distanceM } from "@hoa/geo";

/**
 * La carte du rendez-vous : un plan incliné, sans relief ni nom de rue,
 * qui ne montre que les bâtiments et le chemin.
 *
 *   - Fond : tuiles vectorielles OpenFreeMap (gratuites, sans clé, sans
 *     cookie), réduites aux contours des bâtiments, dans les couleurs du
 *     thème.
 *   - Chemin : calculé à pied par le serveur (`/api/itineraire`, qui
 *     interroge OSRM chez FOSSGIS). Sans réponse, une ligne droite.
 *   - Rendu : MapLibre GL, chargé seulement à cette étape (1 Mo) depuis
 *     `/vendor`, sans dépendance à installer. Sans WebGL, un plan dessiné.
 *
 * Le nord n'est pas en haut : la carte tourne pour que le rendez-vous soit
 * devant soi, comme sur la planche du wireframe.
 */

const VERSION = "5.24.0";
const TUILES = "https://tiles.openfreemap.org/planet";

interface CarteMaplibre {
  on(evt: string, fn: () => void): void;
  remove(): void;
  getSource(id: string): { setData(d: unknown): void } | undefined;
  fitBounds(b: [[number, number], [number, number]], o: Record<string, unknown>): void;
  jumpTo(o: Record<string, unknown>): void;
  isStyleLoaded(): boolean;
}
interface Maplibre {
  Map: new (o: Record<string, unknown>) => CarteMaplibre;
  supported?: () => boolean;
}
declare global {
  interface Window {
    maplibregl?: Maplibre;
  }
}

let chargement: Promise<Maplibre> | null = null;

function chargerMaplibre(): Promise<Maplibre> {
  if (window.maplibregl) return Promise.resolve(window.maplibregl);
  if (chargement) return chargement;
  chargement = new Promise<Maplibre>((ok, ko) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `/vendor/maplibre-gl-${VERSION}/maplibre-gl.css`;
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = `/vendor/maplibre-gl-${VERSION}/maplibre-gl.js`;
    js.async = true;
    js.onload = () => (window.maplibregl ? ok(window.maplibregl) : ko(new Error("maplibre absent")));
    js.onerror = () => {
      chargement = null;
      ko(new Error("maplibre injoignable"));
    };
    document.head.appendChild(js);
  });
  return chargement;
}

export interface Itineraire {
  coordonnees: Array<[number, number]>;
  distanceM: number;
}

/** Le chemin à pied, demandé au serveur ; `null` si personne ne répond. */
async function demanderItineraire(de: Coords, a: Coords): Promise<Itineraire | null> {
  try {
    const r = await fetch("/api/itineraire", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ de: { lat: de.lat, lon: de.lon }, a: { lat: a.lat, lon: a.lon } }),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { itineraire: Itineraire | null };
    return j.itineraire;
  } catch {
    return null;
  }
}

function couleurs(el: HTMLElement): Record<string, string> {
  const s = getComputedStyle(el);
  const v = (n: string, d: string) => s.getPropertyValue(n).trim() || d;
  return {
    fond: v("--surface", "#262626"),
    bati: v("--trait", "#5a5a5a"),
    chemin: v("--texte", "#ffffff"),
    depart: v("--noeud", "#8a8a8a"),
    arrivee: v("--texte", "#ffffff"),
  };
}

const ligne = (c: Array<[number, number]>) => ({
  type: "FeatureCollection",
  features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: c } }],
});
const point = (c: [number, number] | null) => ({
  type: "FeatureCollection",
  features: c ? [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: c } }] : [],
});

export function Carte(props: {
  fix: Fix | null;
  cible: Coords;
  skin: string;
  onItineraire?: (i: Itineraire | null) => void;
  /** En grand : on la déplace, on zoome, on la tourne du doigt. */
  plein?: boolean;
  /** Change pour recadrer sur le trajet (bouton « Recentrer »). */
  recentrer?: number;
  /** En petit : un appui l'ouvre en grand. */
  onAgrandir?: () => void;
}): JSX.Element {
  const boite = useRef<HTMLDivElement>(null);
  const carte = useRef<CarteMaplibre | null>(null);
  const [repli, setRepli] = useState(false);
  const [chemin, setChemin] = useState<Itineraire | null>(null);
  /** Change quand la carte a fini de charger son style : le tracé peut se poser. */
  const [pret, setPret] = useState(0);
  const [credits, setCredits] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(() => setCredits(false), 5000);
    return () => window.clearTimeout(id);
  }, []);
  const origine = useRef<{ at: number; p: Coords } | null>(null);

  // Le chemin : au premier point connu, puis quand on s'est éloigné de
  // quarante mètres de l'origine du dernier calcul (une fois toutes les
  // trente secondes au plus). La réponse est gardée tant que la carte vit,
  // même si la position a bougé entre-temps.
  const { fix, cible, onItineraire } = props;
  const vivante = useRef(true);
  useEffect(() => {
    vivante.current = true;
    return () => {
      vivante.current = false;
    };
  }, []);
  const rappel = useRef(onItineraire);
  rappel.current = onItineraire;
  useEffect(() => {
    if (!fix) return;
    const o = origine.current;
    const maintenant = Date.now();
    if (o && (distanceM(o.p, fix) < 40 || maintenant - o.at < 30_000)) return;
    origine.current = { at: maintenant, p: { lat: fix.lat, lon: fix.lon } };
    void demanderItineraire(fix, cible).then((i) => {
      if (!vivante.current) return;
      setChemin(i);
      rappel.current?.(i);
    });
  }, [fix, cible]);

  // La carte elle-même, recréée quand le thème change.
  useEffect(() => {
    const el = boite.current;
    if (!el) return;
    let fini = false;
    void chargerMaplibre()
      .then((ml) => {
        if (fini) return;
        if (ml.supported && !ml.supported()) throw new Error("webgl");
        const c = couleurs(el);
        const m = new ml.Map({
          container: el,
          interactive: Boolean(props.plein),
          dragRotate: Boolean(props.plein),
          touchPitch: Boolean(props.plein),
          attributionControl: false,
          pitch: 55,
          center: [cible.lon, cible.lat],
          zoom: 16,
          fadeDuration: 0,
          style: {
            version: 8,
            sources: {
              omt: { type: "vector", url: TUILES },
              chemin: { type: "geojson", data: ligne([]) },
              depart: { type: "geojson", data: point(null) },
              arrivee: { type: "geojson", data: point([cible.lon, cible.lat]) },
            },
            layers: [
              { id: "fond", type: "background", paint: { "background-color": c.fond } },
              /* Les rues, fines : visibles même de loin, quand le trajet est
               * long et que les bâtiments (présents à partir du zoom 13 dans
               * les tuiles) ne sont pas encore dessinés. */
              {
                id: "rues",
                type: "line",
                source: "omt",
                "source-layer": "transportation",
                filter: ["!in", "class", "rail", "transit", "ferry", "aerialway"],
                layout: { "line-cap": "round", "line-join": "round" },
                paint: {
                  "line-color": c.bati,
                  "line-opacity": 0.55,
                  "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.6, 14, 1.4, 17, 4],
                },
              },
              {
                id: "batiments",
                type: "line",
                source: "omt",
                "source-layer": "building",
                paint: { "line-color": c.bati, "line-width": 2 },
              },
              {
                id: "chemin",
                type: "line",
                source: "chemin",
                layout: { "line-cap": "round", "line-join": "round" },
                paint: { "line-color": c.chemin, "line-width": 8 },
              },
              { id: "depart", type: "circle", source: "depart", paint: { "circle-radius": 8, "circle-color": c.depart } },
              { id: "arrivee", type: "circle", source: "arrivee", paint: { "circle-radius": 11, "circle-color": c.arrivee } },
            ],
          },
        });
        m.on("error", () => {
          /* tuiles injoignables : le fond reste uni, le chemin se dessine quand même */
        });
        carte.current = m;
        m.on("load", () => setPret((n) => n + 1));
      })
      .catch(() => setRepli(true));
    return () => {
      fini = true;
      carte.current?.remove();
      carte.current = null;
    };
    // `cible` ne change pas pendant une soirée ; le thème, si.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.skin]);

  // Mettre à jour le tracé et le cadrage. En grand, on ne recadre qu'à
  // l'ouverture et sur demande : la carte reste là où le doigt l'a laissée.
  const cadree = useRef<number | null>(null);
  useEffect(() => {
    const m = carte.current;
    if (!m) return;
    const trace: Array<[number, number]> =
      chemin?.coordonnees ?? (fix ? [[fix.lon, fix.lat], [cible.lon, cible.lat]] : []);
    const poser = (): void => {
      m.getSource("chemin")?.setData(ligne(trace));
      m.getSource("depart")?.setData(point(fix ? [fix.lon, fix.lat] : null));
      if (props.plein && cadree.current === (props.recentrer ?? 0) && trace.length >= 2) return;
      if (props.plein && trace.length >= 2) cadree.current = props.recentrer ?? 0;
      if (trace.length < 2 || !fix) {
        m.jumpTo({ center: [cible.lon, cible.lat], zoom: 16, pitch: 55 });
        return;
      }
      const lons = trace.map((c) => c[0]);
      const lats = trace.map((c) => c[1]);
      m.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        { bearing: bearing(fix, cible), pitch: 55, padding: { top: 24, bottom: 24, left: 36, right: 36 }, maxZoom: 17.5, duration: 0 },
      );
    };
    if (m.isStyleLoaded()) poser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chemin, fix, cible, pret, props.recentrer]);

  if (repli) {
    return props.onAgrandir && !props.plein ? (
      <button type="button" className="hoa-carte-bouton" aria-label="Agrandir la carte" onClick={props.onAgrandir}>
        <PlanDessine fix={fix} cible={cible} />
      </button>
    ) : (
      <PlanDessine fix={fix} cible={cible} />
    );
  }
  /* Les crédits de la carte, obligatoires (licence ODbL d'OpenStreetMap) :
   * affichés quelques secondes à l'ouverture, puis retrouvables à tout moment
   * dans Paramètres › Informations. Les règles d'attribution d'OpenStreetMap
   * admettent ce repli, à condition que l'information reste facile à trouver. */
  const carteVue = (
    <div className={props.plein ? "hoa-carte plein" : "hoa-carte"} aria-hidden={props.plein ? undefined : true} aria-label={props.plein ? "Carte du rendez-vous" : undefined}>
      <div className="fond-carte" ref={boite} />
      {credits || props.plein ? <span className="credits">© OpenStreetMap · OpenFreeMap</span> : null}
    </div>
  );
  if (props.onAgrandir && !props.plein) {
    return (
      <button type="button" className="hoa-carte-bouton" aria-label="Agrandir la carte" onClick={props.onAgrandir}>
        {carteVue}
      </button>
    );
  }
  return carteVue;
}

/**
 * La carte en grand, par-dessus l'écran : on s'y déplace librement ;
 * « Recentrer » ramène au trajet, la croix referme.
 */
export function CartePleine(props: { fix: Fix | null; cible: Coords; skin: string; lieu: string; onFermer: () => void }): JSX.Element {
  const [recentrer, setRecentrer] = useState(0);
  const d = props.fix ? Math.round(distanceM(props.fix, props.cible)) : null;
  useCouvre();
  return (
    <div className="hoa-carte-plein" role="dialog" aria-modal="true" aria-label="Le chemin du rendez-vous">
      <div className="entete">
        <div className="tx">
          <b>{props.lieu}</b>
          {d !== null ? <span>{d >= 1000 ? `${(d / 1000).toFixed(1).replace(".", ",")} km` : `${d} m`}</span> : null}
        </div>
        <button type="button" className="hoa-rond" aria-label="Fermer la carte" onClick={props.onFermer}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <Carte fix={props.fix} cible={props.cible} skin={props.skin} plein recentrer={recentrer} />
      <button type="button" className="hoa-go2 recentrer" onClick={() => setRecentrer((n) => n + 1)}>
        Recentrer
      </button>
    </div>
  );
}

/**
 * Sans WebGL : le plan incliné de la planche, avec la seule ligne connue —
 * de soi au rendez-vous, droit devant.
 */
function PlanDessine(props: { fix: Fix | null; cible: Coords }): JSX.Element {
  const d = props.fix ? distanceM(props.fix, props.cible) : null;
  return (
    <div className="hoa-carte plan" aria-hidden="true">
      <div className="incline">
        <svg viewBox="0 0 360 340" preserveAspectRatio="xMidYMax meet">
          <polyline points="180,330 180,70" fill="none" stroke="var(--texte)" strokeWidth="11" strokeLinecap="round" />
          {d !== null ? <circle cx="180" cy="322" r="10" fill="var(--noeud)" /> : null}
          <circle cx="180" cy="70" r="14" fill="var(--texte)" />
        </svg>
      </div>
    </div>
  );
}
